import { memo } from 'react';
import type { InventoryItem, InventoryOverviewItem } from '@/supabase/inventory/types/inventory';
import { getAvailableStock } from '@/supabase/inventory/types/inventory';
import { Pencil, Trash2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { TableCell, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { StockBadge } from './StockBadge';

interface InventoryRowProps {
  item: InventoryOverviewItem;
  // 일괄 삭제 선택(체크박스) — isSelected는 원시값, onToggleSelect는 부모의 안정 콜백이라
  // memo 계약과 공존한다(선택이 바뀐 행만 boolean이 flip되어 재렌더).
  isSelected: boolean;
  onToggleSelect: (id: number) => void;
  // PR3 리뷰 수용: 입출고/조정 콜백은 InventoryOverviewItem을 받는다(모달 배지가 가용 판정에
  // reserved를 써야 함 — MovementModalContainer 참고). 삭제는 reserved가 필요 없어 그대로 둔다.
  onOpenMovement: (item: InventoryOverviewItem) => void;
  onOpenAdjust: (item: InventoryOverviewItem) => void;
  // 수정(상품 정보)·삭제는 reserved가 필요 없어 InventoryItem 그대로.
  onOpenEdit: (item: InventoryItem) => void;
  onDelete: (item: InventoryItem) => void;
}

const won = (n: number) => `${n.toLocaleString('ko-KR')}원`;

// memo 계약: 부모(InventoryTable)는 item·원시값과 콜백을 그대로 전달만 하고, 클로저 생성은
// 이 컴포넌트 내부(onClick={() => onOpenMovement(item)})에서만 한다 — CLAUDE.md 규율 5.
function InventoryRowBase({
  item,
  isSelected,
  onToggleSelect,
  onOpenMovement,
  onOpenAdjust,
  onOpenEdit,
  onDelete,
}: InventoryRowProps) {
  // PR3: available(가용)은 파생값 — state로 미러 저장하지 않고 렌더 시 매번 계산(제약 #7).
  const available = getAvailableStock(item);
  return (
    // hover 명시 지정 — table.tsx 기본 hover:bg-muted/50은 이 레포에 muted 토큰이 없는
    // 죽은 클래스라 행 추적 하이라이트가 아예 없었다(액션 버튼 3개짜리 행이라 추적 필요).
    <TableRow className="hover:bg-slate-50/70">
      <TableCell className="pl-4 pr-0">
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggleSelect(item.id)}
          aria-label={`${item.name} 선택`}
          // 죽은 토큰 회피(bg-primary) — 부족만 보기 체크박스와 동일 명시 지정.
          className="data-[state=checked]:border-slate-900 data-[state=checked]:bg-slate-900 data-[state=checked]:text-white"
        />
      </TableCell>
      <TableCell className="truncate pl-3 font-medium text-slate-900" title={item.name}>
        {item.name}
      </TableCell>
      {/* 정렬·폭·톤은 헤더(InventoryTable)와 1:1 — 2존 리듬(이름 좌 | 데이터 전부 우),
          핵심 숫자(판매가·현재고·예약·가용)는 slate-700 한 톤. 좌측 정렬 숫자·혼합 톤이
          조잡함의 원인이었다(오너 지적). 부족 기준·등록일 컬럼은 레이아웃 개편(2026-07-16,
          가로 스크롤 제거)으로 삭제 — 부족 기준은 수정 모달에서 관리, 가용 뱃지가 이미 반영. */}
      {/* 숫자 셀은 truncate+title 컨테인먼트(codexloop 폴리시 R2) — 현실 값은 4~5자리지만
          데이터형 상한(엑셀 경로 20억·음수 재고 허용)까지 어떤 값도 이웃 컬럼을 침범하지
          않아야 고정 폭 레이아웃이 성립한다. 잘리면 title로 원값 확인. */}
      <TableCell className="overflow-hidden text-right tabular-nums text-slate-700">
        <span className="block truncate" title={won(item.price)}>{won(item.price)}</span>
      </TableCell>
      <TableCell className="overflow-hidden text-right tabular-nums text-slate-700">
        <span className="block truncate" title={item.stock.toLocaleString('ko-KR')}>{item.stock.toLocaleString('ko-KR')}</span>
      </TableCell>
      <TableCell className="overflow-hidden text-right tabular-nums text-slate-700">
        <span className="block truncate" title={item.reserved.toLocaleString('ko-KR')}>{item.reserved.toLocaleString('ko-KR')}</span>
      </TableCell>
      {/* 가용 뱃지: 극단값은 셀에서 클립(overflow-hidden) — 뱃지 내부 truncate는 pill 형태를
          깨서 더 어색하고, 현실 값 범위(±5자리)는 w-20에 전부 들어온다. */}
      <TableCell className="overflow-hidden text-right">
        <StockBadge available={available} threshold={item.low_stock_threshold} />
      </TableCell>
      <TableCell className="pr-4">
        <div className="flex items-center justify-end gap-1.5">
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2.5 text-xs"
            aria-label={`${item.name} 입출고`}
            onClick={() => onOpenMovement(item)}
          >
            입출고
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2.5 text-xs"
            aria-label={`${item.name} 재고 조정`}
            onClick={() => onOpenAdjust(item)}
          >
            조정
          </Button>
          {/* 수정도 아이콘 격하(삭제와 동일 근거) — 저빈도 메타데이터 유지보수 액션이라
              고빈도 텍스트 버튼(입출고·조정)과 무게를 구분한다. hover는 중립 slate —
              이 행에서 red는 파괴 액션 전용 어휘라 수정에 쓰지 않는다. Pencil은 이 레포의
              기존 수정 아이콘(SortableItem·MessageTemplateSection). */}
          <Button
            variant="outline"
            size="sm"
            className="h-7 w-7 p-0 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:bg-slate-100 focus-visible:text-slate-700"
            aria-label={`${item.name} 상품 정보 수정`}
            onClick={() => onOpenEdit(item)}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          {/* 삭제는 아이콘 격하(UI 폴리시 3차) — 저빈도·파괴 액션이 텍스트 버튼으로 1차
              액션(입출고)과 동일 무게 × 전 행 반복되면 빨간 라벨 그리드가 시각 노이즈의
              본체가 된다. 기본은 중립 톤, hover에서만 red(파괴 신호). aria-label 유지. */}
          <Button
            variant="outline"
            size="sm"
            className="h-7 w-7 p-0 text-slate-400 hover:bg-red-50 hover:text-red-600 focus-visible:bg-red-50 focus-visible:text-red-600 focus-visible:ring-2 focus-visible:ring-red-400"
            aria-label={`${item.name} 삭제`}
            onClick={() => onDelete(item)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export const InventoryRow = memo(InventoryRowBase);
